/**
 * Navigation registry (DESIGN.md §10, §12 "NavigationRegistry"): one typed list of
 * sections (groups) → pages (items) → tabs, with route, icon, template, permission, scope and
 * search keywords. NavRail, breadcrumbs, page tabs and the command palette are all generated from
 * it, so a new page or tab shows up everywhere at once. Labels are generic (`L`): apps register
 * i18n keys and localize the registry with `localizeRegistry` before handing it to the shell.
 *
 * Tabs are path segments of their page (`/access/audit/logins`); the page's first ready tab is
 * the page path itself. Path segments survive pages that rewrite their own query string (filters).
 * The root page (`/`) keeps its tabs under `/<page id>/<tab>`.
 */
import type { LucideIcon } from 'lucide-react';

/** Page templates from DESIGN.md §11. Every route declares one. */
export type TemplateId = 'T1' | 'T2' | 'T2b' | 'T3' | 'T4' | 'T5' | 'T6' | 'T7' | 'T8' | 'T9' | 'T10' | 'T11';

/**
 * Who manages a page or tab (admin-console-structure.md §5): `instance` = Instance Admin only,
 * `org` = organisation-scoped (Org Admins inside their org), `both` = mixed. Outside its scope an
 * entry is hidden, never disabled; server guards stay authoritative.
 */
export type NavScope = 'instance' | 'org' | 'both';

/** `planned` = registered so nav, palette and docs are final; renders a placeholder. */
export type NavStatus = 'ready' | 'planned';

/**
 * A view inside a tab (information architecture 2026-10-28): when a page concern has more views than
 * the five-tab limit allows, the views become sections of one tab (a segmented control or section
 * list inside the tab, never a second tab row). Path: `<page>/<tab>/<section>`.
 */
export interface NavSection<L = string> {
  id: string;
  label: L;
  status?: NavStatus;
  /** Defaults to the tab scope. */
  scope?: NavScope;
  keywords?: readonly string[];
}

/** A searchable setting label (command palette) that opens its page, tab and optionally section. */
export interface NavSetting<L = string> {
  id: string;
  label: L;
  /** Section of the owning tab that holds the setting. */
  section?: string;
  keywords?: readonly string[];
}

export interface NavTab<L = string> {
  /** Unique within the page; becomes the path segment. */
  id: string;
  label: L;
  status?: NavStatus;
  /** Defaults to the page scope. */
  scope?: NavScope;
  /** Only shown in multi-organisation mode. */
  multiOrgOnly?: boolean;
  keywords?: readonly string[];
  /** Views of this tab (see NavSection). */
  sections?: readonly NavSection<L>[];
  /** Searchable settings inside this tab. */
  settings?: readonly NavSetting<L>[];
}

export interface NavItem<L = string> {
  id: string;
  label: L;
  icon: LucideIcon;
  /** Route path relative to the app base, starting with `/`. */
  path: string;
  template: TemplateId;
  /** Required permission; items are hidden (not disabled) without it. */
  permission?: string;
  /** Extra words for the command palette. */
  keywords?: readonly string[];
  status?: NavStatus;
  /** Default `both`. */
  scope?: NavScope;
  /** Only shown in multi-organisation mode (e.g. Organisations). */
  multiOrgOnly?: boolean;
  /** Page tabs (DESIGN.md §9.2): concerns of one page, deep-linkable as path segments. */
  tabs?: readonly NavTab<L>[];
  /** Searchable settings of a page without tabs. */
  settings?: readonly NavSetting<L>[];
}

export interface NavGroup<L = string> {
  id: string;
  label: L;
  /** A one-page section (Dashboard): the rail shows its item without a section heading. */
  flat?: boolean;
  items: readonly NavItem<L>[];
}

export type NavRegistry<L = string> = readonly NavGroup<L>[];

export interface BreadcrumbItem {
  label: string;
  /** Omitted for the current (last) item. */
  href?: string;
}

/**
 * Translates every label of a registry (groups, pages, tabs, sections, settings). With `alternate`
 * (the other language's translator) the other-language label is added to the search keywords, so
 * the command palette finds an entry by its English or German name whichever language is active.
 */
export function localizeRegistry<L>(registry: NavRegistry<L>, translate: (label: L) => string, alternate?: (label: L) => string): NavRegistry {
  const words = (label: L, keywords?: readonly string[]): readonly string[] | undefined => {
    if (!alternate) return keywords;
    const other = alternate(label);
    return other === translate(label) ? keywords : [...(keywords ?? []), other];
  };
  const withWords = <T extends { label: L; keywords?: readonly string[] }>(entry: T) => {
    const keywords = words(entry.label, entry.keywords);
    return { ...entry, label: translate(entry.label), ...(keywords ? { keywords } : {}) };
  };
  return registry.map((group) => ({
    ...group,
    label: translate(group.label),
    items: group.items.map((item) => ({
      ...withWords(item),
      settings: item.settings?.map(withWords),
      tabs: item.tabs?.map((tab) => ({
        ...withWords(tab),
        sections: tab.sections?.map(withWords),
        settings: tab.settings?.map(withWords),
      })),
    })),
  }));
}

/** Removes items the user may not see, and groups that end up empty. */
export function filterRegistry<L>(registry: NavRegistry<L>, can: (permission: string) => boolean): NavRegistry<L> {
  return registry
    .map((group) => ({ ...group, items: group.items.filter((item) => !item.permission || can(item.permission)) }))
    .filter((group) => group.items.length > 0);
}

export interface ConsoleMode {
  /** The signed-in admin's reach: `instance` sees everything, `org` only `org`/`both` entries. */
  role: 'instance' | 'org';
  /** Multi-organisation mode; off = organisation-only pages and tabs are hidden. */
  multiOrg: boolean;
}

/**
 * Applies scope and organisation mode: hides pages and tabs outside the admin's role or the
 * current mode, then pages left without tabs and empty sections.
 */
export function scopeRegistry<L>(registry: NavRegistry<L>, mode: ConsoleMode): NavRegistry<L> {
  const shown = (entry: { scope?: NavScope; multiOrgOnly?: boolean }, fallback?: NavScope) =>
    (mode.role === 'instance' || (entry.scope ?? fallback ?? 'both') !== 'instance') && (!entry.multiOrgOnly || mode.multiOrg);
  return registry
    .map((group) => ({
      ...group,
      items: group.items
        .filter((item) => shown(item))
        .map((item) => (item.tabs ? { ...item, tabs: item.tabs
          .map((tab) => (tab.sections ? { ...tab, sections: tab.sections.filter((section) => shown(section, tab.scope ?? item.scope)) } : tab))
          .filter((tab) => shown(tab, item.scope) && (!tab.sections || tab.sections.length > 0)) } : item))
        .filter((item) => !item.tabs || item.tabs.length > 0),
    }))
    .filter((group) => group.items.length > 0);
}

/** The tab a page opens with: its first ready tab, else its first tab. */
export function defaultTab<L>(item: NavItem<L>): NavTab<L> | undefined {
  return item.tabs?.find((tab) => tab.status !== 'planned') ?? item.tabs?.[0];
}

/** Prefix of a page's tab segments (`/` has none of its own, so the root page uses its id). */
const tabBase = (item: NavItem<unknown>): string => (item.path === '/' ? `/${item.id}` : item.path);

/** Path of a tab: the default tab is the page path itself. */
export function tabPath<L>(item: NavItem<L>, tabId: string): string {
  return defaultTab(item)?.id === tabId ? item.path : `${tabBase(item)}/${tabId}`;
}

/** The section a tab opens with: its first ready section. */
export function defaultSection<L>(tab: NavTab<L> | undefined): NavSection<L> | undefined {
  return tab?.sections?.find((section) => section.status !== 'planned') ?? tab?.sections?.[0];
}

/** Path of a section: the first section is the tab path itself, the others add a third segment. */
export function sectionPath<L>(item: NavItem<L>, tab: NavTab<L>, sectionId: string): string {
  return defaultSection(tab)?.id === sectionId ? tabPath(item, tab.id) : `${tabBase(item)}/${tab.id}/${sectionId}`;
}

export interface NavMatch<L = string> {
  group: NavGroup<L>;
  item: NavItem<L>;
  /** The active tab of a tabbed page. */
  tab?: NavTab<L>;
  /** The active section of a tab that has sections. */
  section?: NavSection<L>;
}

const within = (pathname: string, base: string): boolean => pathname === base || pathname.startsWith(`${base}/`);

/** Finds the page (and tab) for a path; the longest matching prefix wins, so tab and detail routes keep their page active. */
export function findNavItem<L>(registry: NavRegistry<L>, pathname: string): NavMatch<L> | undefined {
  let best: { group: NavGroup<L>; item: NavItem<L>; length: number } | undefined;
  for (const group of registry) {
    for (const item of group.items) {
      const length =
        item.path === '/'
          ? pathname === '/'
            ? 1
            : item.tabs && within(pathname, tabBase(item))
              ? tabBase(item).length
              : 0
          : within(pathname, item.path)
            ? item.path.length
            : 0;
      if (length > 0 && (!best || length > best.length)) best = { group, item, length };
    }
  }
  if (!best) return undefined;
  const { group, item } = best;
  const segment = pathname.slice(tabBase(item).length + 1).split('/')[0];
  const tab = item.tabs ? (item.tabs.find((candidate) => candidate.id === segment) ?? defaultTab(item)) : undefined;
  const sectionSegment = pathname.slice(tabBase(item).length + 1).split('/')[1];
  const section = tab?.sections ? (tab.sections.find((candidate) => candidate.id === sectionSegment) ?? defaultSection(tab)) : undefined;
  return { group, item, tab, section };
}

/** Breadcrumb for a path: section › page (› tab) (› extra levels supplied by the page). */
export function breadcrumbFor(registry: NavRegistry, pathname: string, extra: readonly BreadcrumbItem[] = []): BreadcrumbItem[] {
  const match = findNavItem(registry, pathname);
  if (!match) return [...extra];
  const items: BreadcrumbItem[] = match.group.flat ? [] : [{ label: match.group.label }];
  const tab = match.tab && (match.item.tabs?.length ?? 0) > 1 ? match.tab : undefined;
  items.push(tab || extra.length > 0 ? { label: match.item.label, href: match.item.path } : { label: match.item.label });
  const section = tab && (tab.sections?.length ?? 0) > 1 ? match.section : undefined;
  if (tab) items.push(extra.length > 0 || section ? { label: tab.label, href: tabPath(match.item, tab.id) } : { label: tab.label });
  if (section) items.push(extra.length > 0 ? { label: section.label, href: sectionPath(match.item, tab!, section.id) } : { label: section.label });
  return [...items, ...extra];
}

export interface RegistryEntry {
  group: NavGroup;
  item: NavItem;
  /** Set for tab entries (pages with more than one tab). */
  tab?: NavTab;
  path: string;
}

/** Pages and their tabs as flat entries (command palette, docs index). */
export function registryEntries(registry: NavRegistry): RegistryEntry[] {
  return registry.flatMap((group) =>
    group.items.flatMap((item) => [
      { group, item, path: item.path },
      ...(item.tabs && item.tabs.length > 1 ? item.tabs.map((tab) => ({ group, item, tab, path: tabPath(item, tab.id) })) : []),
    ]),
  );
}

export interface SearchEntry {
  group: NavGroup;
  item: NavItem;
  tab?: NavTab;
  section?: NavSection;
  setting?: NavSetting;
  path: string;
}

/**
 * Everything the command palette can open: pages, tabs, sections and settings of one (already
 * role-filtered) registry. Hidden or planned entries never appear, because the registry passed in
 * is the same one that drives the sidebar.
 */
export function searchEntries(registry: NavRegistry): SearchEntry[] {
  const settingPath = (item: NavItem, tab: NavTab | undefined, setting: NavSetting) =>
    !tab ? item.path : setting.section && tab.sections?.some((section) => section.id === setting.section) ? sectionPath(item, tab, setting.section) : tabPath(item, tab.id);
  return registry.flatMap((group) =>
    group.items.flatMap((item): SearchEntry[] => [
      { group, item, path: item.path },
      ...(item.settings ?? []).filter((setting) => setting.label !== item.label).map((setting) => ({ group, item, setting, path: settingPath(item, undefined, setting) })),
      ...(item.tabs ?? []).flatMap((tab): SearchEntry[] => [
        ...(item.tabs!.length > 1 ? [{ group, item, tab, path: tabPath(item, tab.id) }] : []),
        ...(tab.sections && tab.sections.length > 1 ? tab.sections.filter((section) => section.status !== 'planned').map((section) => ({ group, item, tab, section, path: sectionPath(item, tab, section.id) })) : []),
        // A setting named like its tab or section is that entry already; listing it twice only adds noise.
        ...(tab.settings ?? []).filter((setting) => setting.label !== tab.label && !(tab.sections && tab.sections.length > 1 && tab.sections.some((section) => section.id === setting.section && section.label === setting.label))).map((setting) => ({ group, item, tab, setting, path: settingPath(item, tab, setting) })),
      ]),
    ]),
  );
}
